import { CloseOutlined, MenuOutlined, ShoppingCartOutlined } from '@ant-design/icons';
import { UserMenu } from '@components/molecules';
import { CartDrawer } from '@components/organisms';
import { useCartDrawer } from '@contexts';
import { useAuth, useCart } from '@hooks';
import { InstagramLogo, LinkedinLogo, User } from '@phosphor-icons/react';
import { Badge, Skeleton } from 'antd';
import { useEffect, useRef, useState } from 'react';
import { Link, Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';

export const PrivateTemplate = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [headerVisible, setHeaderVisible] = useState(true);
  const lastScrollY = useRef(0);
  const { profile: { data: profile, isLoading } } = useAuth();
  const isAuthenticated = !!profile;
  const { cart } = useCart(isAuthenticated);
  const { openDrawer } = useCartDrawer();

  // Redirect to login if not authenticated
  if (!isLoading && !profile) {
    return <Navigate to='/login' />;
  }

  // Efeito para detectar scroll e ajustar o header
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      // Verifica se a página foi scrollada além de um certo limite (50px)
      setScrolled(currentScrollY > 50);

      // Determina a direção do scroll
      if (currentScrollY > lastScrollY.current && currentScrollY > 100) {
        // Scrolling down & não está no topo
        setHeaderVisible(false);
      } else {
        // Scrolling up ou no topo
        setHeaderVisible(true);
      }

      // Atualiza a posição do último scroll
      lastScrollY.current = currentScrollY;
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Função para verificar se o link está ativo
  const isActiveLink = (path: string) => {
    return location.pathname === path ||
      (path !== '/' && location.pathname.startsWith(path));
  };

  // Função para navegação com scrollToTop
  const navigateWithScroll = (path: string) => {
    navigate(path);
    window.scrollTo(0, 0);
  };

  if (isLoading) {
    return <Skeleton loading className='w-full min-h-screen' />;
  }

  return (
    <div className='bg-second-black-carbon'>
      <header
        className={`bg-second-black-carbon fixed top-0 left-0 w-full z-50 transition-all duration-300
        ${scrolled ? 'py-3 md:py-4' : 'py-3 md:py-4'}
        ${headerVisible ? 'translate-y-0' : '-translate-y-full'}`}
      >
        <div className='container mx-auto px-4 flex justify-between items-center relative'>
          {/* Logo - responsive positioning */}
          <div
            className={`absolute left-4 md:left-4 bottom-0 transition-all duration-300 ${headerVisible ? 'translate-y-1/2 md:translate-y-2/3' : '-translate-y-0'} z-20 cursor-pointer`}
            onClick={() => navigateWithScroll('/')}
          >
            <img
              src='/assets/logos/ruppert-logo.svg'
              alt='Logo Ruppert'
              className='w-12 sm:w-14 md:w-16 lg:w-18 h-auto object-contain'
            />
          </div>

          {/* Spacer for logo */}
          <div className="w-14 sm:w-16 md:w-20" aria-hidden="true" />

          {/* Desktop Navigation */}
          <nav className='hidden xl:flex items-center space-x-4 2xl:space-x-5'>
            <Link to='/' onClick={() => window.scrollTo(0, 0)} className={`font-android text-sm lg:text-base xl:text-lg nav-link whitespace-nowrap ${isActiveLink('/') ? 'text-yellow-principal' : 'text-gray-300'}`}>HOME</Link>
            <Link to='/about' onClick={() => window.scrollTo(0, 0)} className={`font-android text-sm lg:text-base xl:text-lg nav-link whitespace-nowrap ${isActiveLink('/about') ? 'text-yellow-principal' : 'text-gray-300'}`}>ABOUT</Link>
            <Link to='/services' onClick={() => window.scrollTo(0, 0)} className={`font-android text-sm lg:text-base xl:text-lg nav-link whitespace-nowrap ${isActiveLink('/services') ? 'text-yellow-principal' : 'text-gray-300'}`}>SERVICES</Link>
            <Link to='/projects' onClick={() => window.scrollTo(0, 0)} className={`font-android text-sm lg:text-base xl:text-lg nav-link whitespace-nowrap ${isActiveLink('/projects') ? 'text-yellow-principal' : 'text-gray-300'}`}>PROJECTS</Link>
            <Link to='/careers' onClick={() => window.scrollTo(0, 0)} className={`font-android text-sm lg:text-base xl:text-lg nav-link whitespace-nowrap ${isActiveLink('/careers') ? 'text-yellow-principal' : 'text-gray-300'}`}>CAREERS</Link>
            {profile?.profile === 'admin' && (
              <Link to='/merch' onClick={() => window.scrollTo(0, 0)} className={`font-android text-sm lg:text-base xl:text-lg nav-link whitespace-nowrap ${isActiveLink('/merch') ? 'text-yellow-principal' : 'text-gray-300'}`}>SHOP</Link>
            )}
            <Link to='/contact' onClick={() => window.scrollTo(0, 0)} className={`font-android text-sm lg:text-base xl:text-lg nav-link whitespace-nowrap ${isActiveLink('/contact') ? 'text-yellow-principal' : 'text-gray-300'}`}>CONTACT</Link>
          </nav>

          {/* Desktop Actions */}
          <div className='hidden xl:flex items-center gap-3 2xl:gap-4'>
            {/* Cart Icon with Badge */}
            {profile?.profile === 'admin' && (
              <div
                className='bg-yellow-principal p-2 hover-3d cursor-pointer relative flex items-center justify-center'
                onClick={openDrawer}
              >
                <Badge
                  count={cart?.totalItems || 0}
                  showZero={false}
                  style={{ backgroundColor: '#000' }}
                >
                  <ShoppingCartOutlined style={{ fontSize: 20, color: 'black' }} />
                </Badge>
              </div>
            )}

            {isLoading ? (
              <Skeleton.Avatar active size="small" />
            ) : profile ? (
              <UserMenu profile={profile} />
            ) : (
              <div className='bg-yellow-principal p-2 hover-3d cursor-pointer' onClick={() => navigateWithScroll('/login')}>
                <User size={20} color="black" />
              </div>
            )}
            <div
              className='bg-yellow-principal hover-3d py-2 px-4 2xl:px-6 cursor-pointer'
              onClick={() => navigateWithScroll('/quote')}
            >
              <span className='text-black font-android font-bold text-sm 2xl:text-base whitespace-nowrap'>
                GET A QUOTE
              </span>
            </div>
          </div>

          {/* Mobile/Tablet Actions */}
          <div className='flex xl:hidden items-center gap-2 sm:gap-3'>
            {/* Cart Icon for mobile */}
            {profile?.profile === 'admin' && (
              <div
                className='bg-yellow-principal p-1.5 sm:p-2 cursor-pointer flex items-center justify-center'
                onClick={openDrawer}
              >
                <Badge
                  count={cart?.totalItems || 0}
                  showZero={false}
                  style={{ backgroundColor: '#000' }}
                  size="small"
                >
                  <ShoppingCartOutlined style={{ fontSize: 18, color: 'black' }} />
                </Badge>
              </div>
            )}

            {/* User icon for mobile */}
            {isLoading ? (
              <Skeleton.Avatar active size="small" />
            ) : profile ? (
              <UserMenu profile={profile} />
            ) : (
              <div className='bg-yellow-principal p-1.5 sm:p-2 cursor-pointer' onClick={() => navigateWithScroll('/login')}>
                <User size={18} color="black" />
              </div>
            )}

            {/* Menu toggle */}
            <button
              className="text-gray-300 text-xl sm:text-2xl p-1"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <CloseOutlined /> : <MenuOutlined />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="xl:hidden bg-second-black-carbon w-full py-4 px-4 absolute top-full left-0 z-50 shadow-lg border-t border-zinc-800">
            <div className="flex flex-col space-y-3">
              <Link
                to='/'
                className={`font-android text-base sm:text-lg py-2 border-b border-zinc-800 transition duration-300 ${isActiveLink('/') ? 'text-yellow-principal' : 'text-gray-400'}`}
                onClick={() => {
                  setMobileMenuOpen(false);
                  window.scrollTo(0, 0);
                }}
              >
                HOME
              </Link>
              <Link
                to='/about'
                className={`font-android text-base sm:text-lg py-2 border-b border-zinc-800 transition duration-300 ${isActiveLink('/about') ? 'text-yellow-principal' : 'text-gray-400'}`}
                onClick={() => {
                  setMobileMenuOpen(false);
                  window.scrollTo(0, 0);
                }}
              >
                ABOUT
              </Link>
              <Link
                to='/services'
                className={`font-android text-base sm:text-lg py-2 border-b border-zinc-800 transition duration-300 ${isActiveLink('/services') ? 'text-yellow-principal' : 'text-gray-400'}`}
                onClick={() => {
                  setMobileMenuOpen(false);
                  window.scrollTo(0, 0);
                }}
              >
                SERVICES
              </Link>
              <Link
                to='/projects'
                className={`font-android text-base sm:text-lg py-2 border-b border-zinc-800 transition duration-300 ${isActiveLink('/projects') ? 'text-yellow-principal' : 'text-gray-400'}`}
                onClick={() => {
                  setMobileMenuOpen(false);
                  window.scrollTo(0, 0);
                }}
              >
                PROJECTS
              </Link>
              <Link
                to='/careers'
                className={`font-android text-base sm:text-lg py-2 border-b border-zinc-800 transition duration-300 ${isActiveLink('/careers') ? 'text-yellow-principal' : 'text-gray-400'}`}
                onClick={() => {
                  setMobileMenuOpen(false);
                  window.scrollTo(0, 0);
                }}
              >
                CAREERS
              </Link>
              {profile?.profile === 'admin' && (
                <Link
                  to='/merch'
                  className={`font-android text-base sm:text-lg py-2 border-b border-zinc-800 transition duration-300 ${isActiveLink('/merch') ? 'text-yellow-principal' : 'text-gray-400'}`}
                  onClick={() => {
                    setMobileMenuOpen(false);
                    window.scrollTo(0, 0);
                  }}
                >
                  SHOP
                </Link>
              )}
              <Link
                to='/contact'
                className={`font-android text-base sm:text-lg py-2 border-b border-zinc-800 transition duration-300 ${isActiveLink('/contact') ? 'text-yellow-principal' : 'text-gray-400'}`}
                onClick={() => {
                  setMobileMenuOpen(false);
                  window.scrollTo(0, 0);
                }}
              >
                CONTACT
              </Link>

              {/* Get a Quote button in mobile menu */}
              <div
                className='bg-yellow-principal py-3 px-4 cursor-pointer text-center mt-2'
                onClick={() => {
                  setMobileMenuOpen(false);
                  navigateWithScroll('/quote');
                }}
              >
                <span className='text-black font-android font-bold text-base'>
                  GET A QUOTE
                </span>
              </div>

              {/* Admin links */}
              {profile && profile.profile === 'admin' && (
                <Link
                  to='/talent-pool'
                  className={`font-android text-base sm:text-lg py-2 border-b border-zinc-800 transition duration-300 ${isActiveLink('/talent-pool') ? 'text-yellow-principal' : 'text-gray-400'}`}
                  onClick={() => {
                    setMobileMenuOpen(false);
                    window.scrollTo(0, 0);
                  }}
                >
                  TALENT POOL
                </Link>
              )}
            </div>
          </div>
        )}
      </header>

      <main className='flex-1 pt-14 md:pt-16'>
        <Outlet />
      </main>

      <footer className="bg-second-black-carbon text-gray-300 font-barlow">
        <div className="container mx-auto px-4 py-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-12">
            {/* Coluna 1: Logo e Redes Sociais */}
            <div className="flex flex-col items-center sm:col-span-2 lg:col-span-1">
              <img
                src='/assets/logos/ruppert-logo.svg'
                alt='Ruppert Inc. Logo'
                className='w-32 sm:w-40 lg:w-50 h-auto mb-6'
                onClick={() => navigateWithScroll('/')}
                style={{ cursor: 'pointer' }}
              />
              <div className="flex space-x-8 justify-center">
                <a href="https://instagram.com/ruppert.inc" target="_blank" rel="noopener noreferrer" className="social-icon">
                  <InstagramLogo size={24} />
                </a>
                <a href="https://www.linkedin.com/company/ruppert-inc" target="_blank" rel="noopener noreferrer" className="social-icon">
                  <LinkedinLogo size={24} />
                </a>
              </div>
            </div>

            {/* Coluna 2: Quick Links */}
            <div className="text-center sm:text-left">
              <h3 className="font-android font-normal text-white text-lg mb-4">QUICK LINKS</h3>
              <ul className="space-y-2">
                <li><Link to='/' onClick={() => window.scrollTo(0, 0)} className='text-gray-300 hover:text-yellow-principal transition-colors'>Home</Link></li>
                <li><Link to='/about' onClick={() => window.scrollTo(0, 0)} className='text-gray-300 hover:text-yellow-principal transition-colors'>About Us</Link></li>
                <li><Link to='/services' onClick={() => window.scrollTo(0, 0)} className='text-gray-300 hover:text-yellow-principal transition-colors'>Services</Link></li>
                <li><Link to='/careers' onClick={() => window.scrollTo(0, 0)} className='text-gray-300 hover:text-yellow-principal transition-colors'>Careers</Link></li>
                {profile?.profile === 'admin' && (
                  <li><Link to='/merch' onClick={() => window.scrollTo(0, 0)} className='text-gray-300 hover:text-yellow-principal transition-colors'>Shop</Link></li>
                )}
                <li><Link to='/contact' onClick={() => window.scrollTo(0, 0)} className='text-gray-300 hover:text-yellow-principal transition-colors'>Contact</Link></li>
                <li><Link to='/quote' onClick={() => window.scrollTo(0, 0)} className='text-gray-300 hover:text-yellow-principal transition-colors'>Get a Quote</Link></li>
              </ul>
            </div>

            {/* Coluna 3: Contact Us */}
            <div className="text-center sm:text-left">
              <h3 className="font-android font-normal text-white text-lg mb-4">CONTACT US</h3>
              <ul className="space-y-5">
                <li className="flex items-start justify-center sm:justify-start">
                  <img src="/assets/icons/location-icon.svg" alt="Location icon" className="h-7 mr-2 pt-1" />
                  <span>PO Box 1921<br />Truckee, CA, USA 96160</span>
                </li>
                <li className="flex items-center justify-center sm:justify-start">
                  <img src="/assets/icons/phone-icon.svg" alt="Phone icon" className="h-7 mr-2 pb-1" />
                  <a href="tel:+15305874509" className='text-gray-300 hover:text-yellow-principal transition-colors'>+1 530.587.4509</a>
                </li>
                <li className="flex items-center justify-center sm:justify-start">
                  <img src="/assets/icons/mail-icon.svg" alt="Mail icon" className="h-8 mr-2 py-1" />
                  <a href="mailto:info@ruppertinc.com" className='text-gray-300 hover:text-yellow-principal transition-colors'>contact@ruppertinc.com</a>
                </li>
              </ul>
            </div>
          </div>

          <hr className="border-gray-700 my-8 md:my-10" />

          <div className="flex flex-col items-center">
            <p className="text-gray-400 text-sm mb-4 text-center">
              © {new Date().getFullYear()} Ruppert Inc. All rights reserved.
            </p>
          </div>
        </div>
      </footer>

      {/* Cart Drawer */}
      <CartDrawer />
    </div>
  );
};
